/**
 * useInvoiceShipment - Hook para prefacturar shipments individuales
 * 
 * Permite prefacturar un shipment específico mediante POST a /invoices/pre-invoices/{id}/invoice.
 * Muestra toasts de éxito/error y ejecuta callback de refetch al completar exitosamente.
 * Usado desde el dropdown de acciones de cada shipment.
 * 
 * @returns {Object} { invoiceShipment: función de prefacturación, isInvoicing: estado de carga }
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { ShipmentsApiAdapter } from '../adapters/ShipmentsApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';
export const useInvoiceShipment = () => {
  const { t } = useTranslation('invoicesOrdersModule');
  const { toast } = useToast();
  const [isInvoicing, setIsInvoicing] = useState(false);

  const invoiceShipment = useCallback(
    async (shipmentId: number, onSuccess?: () => void) => {
      setIsInvoicing(true);

      try {
        const shipmentsAdapter = new ShipmentsApiAdapter(
          createHttpClient(apiClientWH)
        );
        await shipmentsAdapter.invoiceShipment(shipmentId);

        // 🎯 Si llegamos aquí, la API devolvió 200 OK = éxito
        toast({
          title: t('toast.invoice_success.title', 'Shipment prefacturado'),
          description: t('toast.invoice_success.description', 'El shipment se ha prefacturado exitosamente'),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch de datos)
        onSuccess?.();
      } catch (error: any) {
        // 🎯 Extraer mensaje amigable de la API
        const apiMessage: string | undefined = error?.response?.data?.message;
        const apiErrors: Record<string, string[] | string> | undefined =
          error?.response?.data?.errors;

        let description = apiMessage;

        // Unir los errores detallados si existen
        if (apiErrors && typeof apiErrors === 'object') {
          const details = Object.values(apiErrors)
            .flat()
            .filter(Boolean)
            .join(' • ');
          description = description ? `${description} — ${details}` : details;
        }

        // Fallback de mensaje
        if (!description) {
          description =
            error?.message ||
            t(
              'toast.invoice_error.unexpected',
              'Error inesperado al prefacturar el shipment. Inténtalo de nuevo.'
            );
        }

        toast({
          title: t('toast.invoice_error.title', 'Error al prefacturar'),
          description,
          variant: 'destructive',
        });
      } finally {
        setIsInvoicing(false);
      }
    },
    [t, toast]
  );

  return {
    invoiceShipment,
    isInvoicing,
  };
};

